import * as React from 'react';
import { format, startOfDay } from 'date-fns';
import { CalendarIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export type DatePickerProps = {
  value?: Date;
  onChange: (date: Date | undefined) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
  className?: string;
  placeholder?: string;
  /** Raise the portalled calendar above a full-screen dialog. */
  positionerClassName?: string;
};

function DatePicker({
  value,
  onChange,
  minDate,
  maxDate,
  disabled,
  id,
  'aria-label': ariaLabel,
  className,
  placeholder = 'MM/DD/YYYY',
  positionerClassName,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<string | null>(null);

  const disabledMatchers = React.useMemo(() => {
    const matchers: Array<{ before: Date } | { after: Date }> = [];
    if (minDate) {
      matchers.push({ before: startOfDay(minDate) });
    }
    if (maxDate) {
      matchers.push({ after: startOfDay(maxDate) });
    }
    return matchers.length > 0 ? matchers : undefined;
  }, [minDate, maxDate]);

  const commit = (text: string) => {
    const trimmed = text.trim();
    setDraft(null);
    if (!trimmed) {
      onChange(undefined);
      return;
    }
    const parsed = parseTypedDate(trimmed);
    if (parsed) onChange(parsed);
  };

  return (
    <div
      className={cn(
        'tw:inline-flex tw:w-fit tw:items-center tw:gap-1',
        className
      )}
    >
      <Input
        id={id}
        value={draft ?? (value ? format(value, 'MMM d, yyyy') : '')}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={(event) => commit(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          commit(event.currentTarget.value);
        }}
        className="tw:w-36"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled={disabled}
              aria-label="Open calendar"
            />
          }
        >
          <CalendarIcon className="tw:size-4" />
        </PopoverTrigger>
        <PopoverContent
          className="dark tw:w-auto tw:p-0"
          positionerClassName={positionerClassName}
          align="start"
        >
          <Calendar
            mode="single"
            selected={value}
            onSelect={(date) => {
              setDraft(null);
              onChange(date);
              setOpen(false);
            }}
            disabled={disabledMatchers}
            defaultMonth={value}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function parseTypedDate(text: string): Date | undefined {
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text);
  if (iso) return calendarDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  const us = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/.exec(text);
  if (us) {
    const year = Number(us[3].length === 2 ? `20${us[3]}` : us[3]);
    return calendarDate(year, Number(us[1]), Number(us[2]));
  }
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return calendarDate(
    parsed.getFullYear(),
    parsed.getMonth() + 1,
    parsed.getDate()
  );
}

function calendarDate(
  year: number,
  month: number,
  day: number
): Date | undefined {
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return undefined;
  }
  return date;
}

export { DatePicker };
